import { useState } from "react";
import { toast } from "sonner";
import { emailTemplateService } from "@/services/communication/EmailTemplateService";
import { EmailTemplateType, TemplateConfig } from "@/types/email-templates";

interface UseEmailTemplateOperationsProps {
  userProfileId: string | undefined;
  selectedAgentId: string | null;
  agents: any[];
  teamBranding?: any;
}

export const useEmailTemplateOperations = ({
  userProfileId,
  selectedAgentId,
  agents,
  teamBranding,
}: UseEmailTemplateOperationsProps) => {
  const [saving, setSaving] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);

  const handleSave = async (templateType: EmailTemplateType, config: TemplateConfig) => {
    if (!userProfileId) return;

    try {
      setSaving(true);
      await emailTemplateService.saveTemplateConfig(userProfileId, templateType, config, selectedAgentId);
      toast.success("Template configuration saved successfully");
    } catch (error: any) {
      console.error("Failed to save template config:", error);
      toast.error(error.message || "Failed to save template configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleApplyBranding = async (config: TemplateConfig, onConfigUpdate: (newConfig: TemplateConfig) => void) => {
    try {
      if (selectedAgentId) {
        const agent = agents.find((a) => a.id === selectedAgentId);
        if (!agent || !agent.branding) {
          toast.info(`${agent?.name || "Agent"} has no branding configured. Please configure branding for this agent first.`);
          return;
        }

        const { config: brandedConfig, hasBranding } = await emailTemplateService.applyAgentBranding(
          agent.branding,
          agent.name,
          config,
        );
        onConfigUpdate(brandedConfig);

        if (hasBranding) {
          toast.success(`Applied ${agent.name}'s branding to template`);
        }
      } else if (teamBranding) {
        const { config: brandedConfig, hasBranding } = await emailTemplateService.applyTeamBranding(teamBranding, config);
        onConfigUpdate(brandedConfig);

        if (hasBranding) {
          toast.success("Applied team branding to template");
        } else {
          toast.info("Team has no branding configured.");
        }
      }
    } catch (error) {
      console.error("Failed to apply branding:", error);
      toast.error("Failed to apply branding");
    }
  };

  const handleCloneDefault = async (
    templateType: EmailTemplateType,
    onConfigUpdate: (newConfig: TemplateConfig) => void,
  ) => {
    if (!userProfileId) return;

    try {
      setCloning(true);

      const clonedConfig = await emailTemplateService.cloneDefaultTemplate(
        userProfileId,
        templateType,
        selectedAgentId,
      );

      if (selectedAgentId) {
        const agent = agents.find((a) => a.id === selectedAgentId);
        if (agent?.branding) {
          const { config: brandedConfig, hasBranding } = await emailTemplateService.applyAgentBranding(
            agent.branding,
            agent.name,
            clonedConfig,
          );
          onConfigUpdate(brandedConfig);
          setShowCloneDialog(false);
          if (hasBranding) {
            toast.success("Template cloned successfully with branding applied");
          }
        } else {
          onConfigUpdate(clonedConfig);
          setShowCloneDialog(false);
          toast.info("Template cloned successfully. No branding configured for this agent.");
        }
      } else {
        onConfigUpdate(clonedConfig);
        setShowCloneDialog(false);
        toast.success("Template cloned successfully");
      }
    } catch (error: any) {
      console.error("Failed to clone template:", error);
      toast.error(error.message || "Failed to clone template");
    } finally {
      setCloning(false);
    }
  };

  return {
    saving,
    cloning,
    showCloneDialog,
    setShowCloneDialog,
    handleSave,
    handleApplyBranding,
    handleCloneDefault,
  };
};
